import React, { useState, useMemo } from "react";
import { Plane, Hotel, Calendar, Edit2, Save, X, User } from "lucide-react";
import { format } from "date-fns";
import { Pagination } from "../components/common/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import PrimaryButton from "../components/ui/PrimaryButton";
import {
  usePlayerDetails,
  useUpdatePlayerDetailsByPlayerId,
  useCreatePlayerDetails,
} from "../hooks/usePlayerDetails";
import { useCustomers } from "../hooks/useCustomers";
import { useAgents } from "../hooks/useAgents";
import {
  PlayerDetails as PlayerDetailsType,
  CreatePlayerDetailsRequest,
  UpdatePlayerDetailsRequest,
} from "../api/services/playerDetails/types";

export const PlayerDetails: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<
    CreatePlayerDetailsRequest | UpdatePlayerDetailsRequest
  >({
    notes: "",
    agentId: 0,
    playerId: 0,
    arrivalDate: new Date().toISOString(),
    arrivalTime: new Date().toISOString(),
    flightName: "",
    flightNumber: "",
    stayHotel: "",
    stayDuration: 0,
  });

  const itemsPerPage = 10;

  // Fetch data
  const { data: playerDetailsData, isLoading } = usePlayerDetails({
    skip: (currentPage - 1) * itemsPerPage,
    limit: itemsPerPage,
  });

  const { data: customers } = useCustomers();
  const { data: agents } = useAgents();

  // Mutations
  const createMutation = useCreatePlayerDetails();
  const updateMutation = useUpdatePlayerDetailsByPlayerId();

  // Filter player details based on search
  const filteredDetails = useMemo(() => {
    if (!playerDetailsData?.data) return [];
    if (!searchTerm) return playerDetailsData.data;

    return playerDetailsData.data.filter((detail) => {
      const searchLower = searchTerm.toLowerCase();
      const hotelMatch = detail.stayHotel?.toLowerCase().includes(searchLower);
      const flightMatch = detail.flightName
        ?.toLowerCase()
        .includes(searchLower);
      const flightNumMatch = detail.flightNumber
        ?.toLowerCase()
        .includes(searchLower);

      return hotelMatch || flightMatch || flightNumMatch;
    });
  }, [playerDetailsData?.data, searchTerm]);

  const totalPages = useMemo(() => {
    return Math.ceil((playerDetailsData?.total || 0) / itemsPerPage) || 1;
  }, [playerDetailsData?.total]);

  const handleEdit = (detail: PlayerDetailsType) => {
    setEditingId(detail.id);
    setFormData({
      notes: detail.notes || "",
      agentId: detail.agentId,
      playerId: detail.playerId,
      arrivalDate: detail.arrivalDate,
      arrivalTime: detail.arrivalTime,
      flightName: detail.flightName || "",
      flightNumber: detail.flightNumber || "",
      stayHotel: detail.stayHotel || "",
      stayDuration: detail.stayDuration || 0,
    });
  };

  const handleSave = async (playerId: number) => {
    try {
      await updateMutation.mutateAsync({
        playerId,
        data: formData as UpdatePlayerDetailsRequest,
      });
      setEditingId(null);
    } catch (error) {
      console.error("Error saving player details:", error);
    }
  };

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync(formData as CreatePlayerDetailsRequest);
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error("Error creating player details:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      notes: "",
      agentId: 0,
      playerId: 0,
      arrivalDate: new Date().toISOString(),
      arrivalTime: new Date().toISOString(),
      flightName: "",
      flightNumber: "",
      stayHotel: "",
      stayDuration: 0,
    });
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM dd, yyyy 'at' h:mm a");
    } catch {
      return "N/A";
    }
  };

  const formatDateOnly = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  const formatTimeOnly = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "HH:mm");
    } catch {
      return "";
    }
  };

  const getPlayerName = (playerId: number) => {
    const player = customers?.items?.find(
      (c: { id: string; name: string }) => c.id === playerId.toString(),
    );
    return player?.name || `Player ${playerId}`;
  };

  const getAgentName = (agentId: number) => {
    const agent = agents?.items?.find(
      (a: { id: string; name: string }) => a.id === agentId.toString(),
    );
    return agent?.name || "N/A";
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">
            Player Travel & Accommodation Details
          </h1>
          <p className="text-slate-400 mt-1">
            Manage arrival information, flight details, and hotel stays for all
            players
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by hotel, flight name or number..."
              />
            </div>
            <PrimaryButton onClick={() => setShowCreateModal(true)}>
              <Plane className="w-4 h-4 mr-2" />
              Add Player Details
            </PrimaryButton>
          </div>
        </div>

        {/* Details Table */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="px-6 py-12 text-center">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            </div>
          ) : filteredDetails.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-400">
              {searchTerm
                ? "No player details found matching your search"
                : "No player details added yet"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Flight
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Arrival
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Hotel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredDetails.map((detail) => (
                    <tr
                      key={detail.id}
                      className="hover:bg-slate-700/50 transition-colors"
                    >
                      {editingId === detail.id ? (
                        // Edit Mode
                        <>
                          <td className="px-6 py-4" colSpan={7}>
                            <div className="space-y-4">
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-xs text-slate-400 mb-1">
                                    Flight Name
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.flightName || ""}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        flightName: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-400 mb-1">
                                    Flight Number
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.flightNumber || ""}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        flightNumber: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-400 mb-1">
                                    Arrival Date
                                  </label>
                                  <input
                                    type="date"
                                    value={formatDateOnly(
                                      formData.arrivalDate || "",
                                    )}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        arrivalDate: new Date(
                                          e.target.value,
                                        ).toISOString(),
                                      })
                                    }
                                    className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-xs text-slate-400 mb-1">
                                    Arrival Time
                                  </label>
                                  <input
                                    type="time"
                                    value={formatTimeOnly(
                                      formData.arrivalTime || "",
                                    )}
                                    onChange={(e) => {
                                      const [hours, minutes] =
                                        e.target.value.split(":");
                                      const date = new Date(
                                        formData.arrivalDate || new Date(),
                                      );
                                      date.setHours(
                                        parseInt(hours),
                                        parseInt(minutes),
                                      );
                                      setFormData({
                                        ...formData,
                                        arrivalTime: date.toISOString(),
                                      });
                                    }}
                                    className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-400 mb-1">
                                    Hotel
                                  </label>
                                  <input
                                    type="text"
                                    value={formData.stayHotel || ""}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        stayHotel: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-400 mb-1">
                                    Stay Duration (days)
                                  </label>
                                  <input
                                    type="number"
                                    value={formData.stayDuration || ""}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        stayDuration:
                                          parseInt(e.target.value) || 0,
                                      })
                                    }
                                    className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">
                                  Notes
                                </label>
                                <textarea
                                  value={formData.notes || ""}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      notes: e.target.value,
                                    })
                                  }
                                  rows={2}
                                  className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => setEditingId(null)}
                                  className="px-3 py-1 text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 rounded"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleSave(detail.playerId)}
                                  className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </td>
                        </>
                      ) : (
                        // View Mode
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <User className="w-4 h-4 text-slate-400 mr-2" />
                              <span className="text-white font-medium">
                                {getPlayerName(detail.playerId)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-slate-300">
                              {getAgentName(detail.agentId)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Plane className="w-4 h-4 text-slate-400 mr-2" />
                              <span className="text-slate-300">
                                {detail.flightName && detail.flightNumber
                                  ? `${detail.flightName} ${detail.flightNumber}`
                                  : "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 text-slate-400 mr-2" />
                              <span className="text-slate-300 text-sm">
                                {formatDateTime(detail.arrivalDate)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Hotel className="w-4 h-4 text-slate-400 mr-2" />
                              <span className="text-slate-300">
                                {detail.stayHotel || "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-slate-300">
                              {detail.stayDuration
                                ? `${detail.stayDuration} days`
                                : "N/A"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleEdit(detail)}
                              className="text-blue-400 hover:text-blue-300"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-slate-800 rounded-lg max-w-2xl w-full">
              <div className="p-6 border-b border-slate-700">
                <h2 className="text-lg font-semibold text-white">
                  Add Player Travel Details
                </h2>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Player *
                    </label>
                    <select
                      value={formData.playerId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          playerId: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="0">Select Player</option>
                      {customers?.items?.map(
                        (customer: { id: string; name: string }) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.name}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Agent *
                    </label>
                    <select
                      value={formData.agentId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          agentId: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="0">Select Agent</option>
                      {agents?.items?.map(
                        (agent: { id: string; name: string }) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.name}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Flight Name
                    </label>
                    <input
                      type="text"
                      value={formData.flightName || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, flightName: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="e.g., Emirates"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Flight Number
                    </label>
                    <input
                      type="text"
                      value={formData.flightNumber || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          flightNumber: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="e.g., EK505"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Arrival Date
                    </label>
                    <input
                      type="date"
                      value={formatDateOnly(formData.arrivalDate || "")}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          arrivalDate: new Date(e.target.value).toISOString(),
                        })
                      }
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Arrival Time
                    </label>
                    <input
                      type="time"
                      value={formatTimeOnly(formData.arrivalTime || "")}
                      onChange={(e) => {
                        const [hours, minutes] = e.target.value.split(":");
                        const date = new Date(
                          formData.arrivalDate || new Date(),
                        );
                        date.setHours(parseInt(hours), parseInt(minutes));
                        setFormData({
                          ...formData,
                          arrivalTime: date.toISOString(),
                        });
                      }}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Hotel Name
                    </label>
                    <input
                      type="text"
                      value={formData.stayHotel || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, stayHotel: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="e.g., Marriott Hotel"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Stay Duration (Days)
                    </label>
                    <input
                      type="number"
                      value={formData.stayDuration || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          stayDuration: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="e.g., 7"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none"
                    placeholder="Special requirements, preferences, etc."
                  />
                </div>
              </div>

              <div className="flex gap-3 p-6 border-t border-slate-700">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
